/**
 * Arithmetic expression evaluator for numeric inputs, ported from the Figma editor's
 * `web/js/lib/math_parser.ts` so the compositor accepts the same expressions as the canvas:
 * `+ - * / ^`, parentheses, unary signs, and the relative operators `x` (multiply the current
 * value, e.g. `2x`), `%` (percentage of the current value, e.g. `50%`) plus `px` as a no-op unit.
 * `mixed` resolves to the current value, matching the placeholder shown for a mixed selection.
 *
 * Differences from the original: `const enum` is widened to `enum` (`isolatedModules`), the
 * localized-`mixed` substitution is dropped because the expression is lowercased before tokenizing
 * and our placeholder is the untranslated word, and only the evaluator is exported — the tokenizer
 * and its token types have no callers here.
 */

export enum ErrorType {
  NONE,
  SYNTAX_EMPTY,
  SYNTAX_UNRECOGNIZED,
  SYNTAX_MISMATCHED_PARENS,
  EVAL_UNEXPECTED_TYPE,
  EVAL_EXPECTED_MORE_ARGUMENTS,
  EVAL_IMAGINARY,
  EVAL_UNKNOWN,
  EVAL_NO_CURRENT_VALUE,
  EVAL_INFINITE,
  EVAL_NAN,
}

interface ErrorInfo {
  type: ErrorType;

  /** The problematic range in the input */
  position?: number;
  length?: number;
}

interface AnswerSuccess {
  value: number;
  error: null;
}

interface AnswerError {
  value: null;
  error: ErrorInfo;
}

export type Answer = AnswerSuccess | AnswerError;

/**
 * Evaluates a mathematical expression and returns the result. If the result's `error` property is
 * null, then the `value` property is guaranteed to be a finite non-NaN number.
 */
export function evaluateExpression(expression: string, currentValue?: number): Answer {
  const tokens: Token[] = [];
  const error = tokenize(expression, tokens);

  if (error !== null) {
    return { value: null, error };
  }

  const output: number[] = [];
  const stack: Token[] = [];

  for (const token of tokens) {
    switch (token.type) {
      case TokenType.NUMBER: {
        // If the token is a number, then add it to the output queue.
        output.push(token.value);
        break;
      }

      case TokenType.MIXED_VARIABLE: {
        if (!currentValue && currentValue !== 0) {
          return { value: null, error: { type: ErrorType.EVAL_NO_CURRENT_VALUE } };
        }
        output.push(currentValue);
        break;
      }

      case TokenType.OPERATOR: {
        const operator = operators[token.operator];
        let t: Token | undefined;

        // If the token is an operator, o1, then:
        switch (token.operator) {
          case OperatorType.UNARY_PLUS: {
            // no-op
            break;
          }

          case OperatorType.PAREN_L: {
            // If the token is a left parenthesis, then push it onto the stack.
            stack.push(token);
            break;
          }

          case OperatorType.PAREN_R: {
            // If the token is a right parenthesis:
            if (!stack.length) {
              return {
                value: null,
                error: { type: ErrorType.SYNTAX_MISMATCHED_PARENS, position: token.position },
              };
            }
            while (stack.length) {
              const top = stack[stack.length - 1];

              // Until the token at the top of the stack is a left parenthesis, pop operators off the stack onto the output queue.
              if (top.operator === OperatorType.PAREN_L) {
                // Pop the left parenthesis from the stack, but not onto the output queue.
                stack.pop();
                break;
              } else {
                // If the token at the top of the stack is a function token, pop it onto the output queue.
                const type = evaluate(top.operator, output, currentValue);
                if (type !== ErrorType.NONE) {
                  return { value: null, error: { type } };
                }
                stack.pop();
              }

              // If the stack runs out without finding a left parenthesis, then there are mismatched parentheses.
              if (!stack.length) {
                return {
                  value: null,
                  error: { type: ErrorType.SYNTAX_MISMATCHED_PARENS, position: token.position },
                };
              }
            }
            break;
          }

          default: {
            // while there is an operator token, o2, at the top of the operator stack, and either
            while ((t = stack[stack.length - 1])) {
              // o1 is left-associative and its precedence is less than or equal to that of o2, or
              if (operator.associativity === Associativity.LEFT) {
                if (operator.precedence <= operators[t.operator].precedence) {
                  // then pop o2 off the operator stack, onto the output queue;
                  const type = evaluate(t.operator, output, currentValue);
                  if (type !== ErrorType.NONE) {
                    return { value: null, error: { type } };
                  }
                  stack.pop();
                } else {
                  break;
                }
              } else if (operator.associativity === Associativity.RIGHT) {
                // o1 is right associative, and has precedence less than that of o2,
                if (operator.precedence < operators[t.operator].precedence) {
                  // then pop o2 off the operator stack, onto the output queue;
                  const type = evaluate(t.operator, output, currentValue);
                  if (type !== ErrorType.NONE) {
                    return { value: null, error: { type } };
                  }
                  stack.pop();
                } else {
                  break;
                }
              } else {
                // Should not get here
                return { value: null, error: { type: ErrorType.EVAL_UNKNOWN } };
              }
            }

            // push o1 onto the operator stack.
            stack.push(token);
            break;
          }
        }
        break;
      }

      default: {
        // should not get here.
        return { value: null, error: { type: ErrorType.SYNTAX_UNRECOGNIZED } };
      }
    }
  }

  // When there are no more tokens to read:
  // While there are still operator tokens in the stack:
  while (stack.length) {
    const token = stack.pop();
    if (token === undefined) break;

    // If the operator token on the top of the stack is a parenthesis, then there are mismatched parentheses.
    if (token.operator === OperatorType.PAREN_L || token.operator === OperatorType.PAREN_R) {
      return {
        value: null,
        error: { type: ErrorType.SYNTAX_MISMATCHED_PARENS, position: expression.length },
      };
    }

    // Pop the operator onto the output queue.
    const type = evaluate(token.operator, output, currentValue);
    if (type !== ErrorType.NONE) {
      return { value: null, error: { type } };
    }
  }

  if (output.length !== 1) {
    return { value: null, error: { type: ErrorType.SYNTAX_UNRECOGNIZED } };
  }

  const value = output[0];

  if (Number.isNaN(value)) {
    return { value: null, error: { type: ErrorType.EVAL_NAN } };
  }

  if (!Number.isFinite(value)) {
    return { value: null, error: { type: ErrorType.EVAL_INFINITE } };
  }

  return { value, error: null };
}

////////////////////////////////////////////////////////////////////////////////
// Evaluation

enum OperatorType {
  NONE,
  PAREN_L,
  PAREN_R,
  ADD,
  SUBTRACT,
  MULTIPLY,
  DIVIDE,
  UNARY_PLUS,
  UNARY_MINUS,
  EXPONENT,
  PERCENT,
  X,
  PIXELS,
}

enum Associativity {
  NONE,
  LEFT,
  RIGHT,
}

interface Operator {
  associativity: Associativity;
  precedence: number;
  degree: number;
  name: string;
}

const operators = [
  { associativity: Associativity.NONE, precedence: -1, degree: 0, name: '' },
  { associativity: Associativity.NONE, precedence: -1, degree: 0, name: '(' },
  { associativity: Associativity.NONE, precedence: -1, degree: 0, name: ')' },
  { associativity: Associativity.LEFT, precedence: 1, degree: 2, name: 'add' },
  { associativity: Associativity.LEFT, precedence: 1, degree: 2, name: 'sub' },
  { associativity: Associativity.LEFT, precedence: 2, degree: 2, name: 'mul' },
  { associativity: Associativity.LEFT, precedence: 2, degree: 2, name: 'div' },
  { associativity: Associativity.LEFT, precedence: 4, degree: 1, name: 'pos' },
  { associativity: Associativity.LEFT, precedence: 4, degree: 1, name: 'neg' },
  { associativity: Associativity.RIGHT, precedence: 5, degree: 2, name: 'exp' },
  { associativity: Associativity.RIGHT, precedence: 6, degree: 1, name: '%' },
  { associativity: Associativity.RIGHT, precedence: 6, degree: 1, name: 'x' },
  { associativity: Associativity.RIGHT, precedence: 6, degree: 1, name: 'px' },
] as const satisfies Operator[];

function evaluate(type: OperatorType, values: number[], currentValue?: number): ErrorType {
  if (values.length < operators[type].degree) {
    return ErrorType.EVAL_EXPECTED_MORE_ARGUMENTS;
  }

  switch (type) {
    case OperatorType.NONE:
    case OperatorType.PAREN_L:
    case OperatorType.PAREN_R: {
      return ErrorType.EVAL_UNEXPECTED_TYPE;
    }

    case OperatorType.ADD:
    case OperatorType.SUBTRACT:
    case OperatorType.MULTIPLY:
    case OperatorType.DIVIDE:
    case OperatorType.EXPONENT: {
      const b = values.pop() as number;
      const a = values.pop() as number;
      switch (type) {
        case OperatorType.ADD:
          values.push(a + b);
          break;
        case OperatorType.SUBTRACT:
          values.push(a - b);
          break;
        case OperatorType.MULTIPLY:
          values.push(a * b);
          break;
        case OperatorType.DIVIDE:
          values.push(a / b);
          break;
        case OperatorType.EXPONENT: {
          const d = Math.pow(a, b);
          if (isNaN(d)) return ErrorType.EVAL_IMAGINARY;
          values.push(d);
          break;
        }
      }
      break;
    }

    case OperatorType.UNARY_PLUS:
    case OperatorType.PIXELS: {
      // no-operator
      break;
    }

    case OperatorType.UNARY_MINUS: {
      values.push(-(values.pop() as number));
      break;
    }

    case OperatorType.PERCENT: {
      // We can't multiply by percentages if the current value is null or NaN.
      if (!currentValue && currentValue !== 0) {
        return ErrorType.EVAL_NO_CURRENT_VALUE;
      }
      values.push((currentValue * (values.pop() as number)) / 100);
      break;
    }

    case OperatorType.X: {
      // We can't perform relative multiplication if the current value is null or NaN.
      if (!currentValue && currentValue !== 0) {
        return ErrorType.EVAL_NO_CURRENT_VALUE;
      }
      values.push(currentValue * (values.pop() as number));
      break;
    }
  }

  return ErrorType.NONE;
}

////////////////////////////////////////////////////////////////////////////////
// Parsing

enum TokenType {
  NONE,
  OPERATOR,
  NUMBER,
  MIXED_VARIABLE,
}

enum TokenId {
  NONE,
  OPEN_PAREN,
  CLOSE_PAREN,
  PLUS,
  MINUS,
  ASTERISK,
  SLASH,
  CARET,
  PERCENT,
  X,
  PIXELS,
}

interface Token {
  text: string;
  position: number;
  id: TokenId;
  type: TokenType;
  operator: OperatorType;
  value: number;
}

const textToTokenId: { [text: string]: TokenId } = {
  '(': TokenId.OPEN_PAREN,
  ')': TokenId.CLOSE_PAREN,
  '+': TokenId.PLUS,
  '-': TokenId.MINUS,
  '*': TokenId.ASTERISK,
  '/': TokenId.SLASH,
  '^': TokenId.CARET,
  '%': TokenId.PERCENT,
  x: TokenId.X,
  X: TokenId.X,
  px: TokenId.PIXELS,
};

const tokenIdToOperatorType: { [id: number]: OperatorType } = {
  [TokenId.OPEN_PAREN]: OperatorType.PAREN_L,
  [TokenId.CLOSE_PAREN]: OperatorType.PAREN_R,
  [TokenId.PLUS]: OperatorType.ADD,
  [TokenId.MINUS]: OperatorType.SUBTRACT,
  [TokenId.ASTERISK]: OperatorType.MULTIPLY,
  [TokenId.SLASH]: OperatorType.DIVIDE,
  [TokenId.CARET]: OperatorType.EXPONENT,
  [TokenId.PERCENT]: OperatorType.PERCENT,
  [TokenId.X]: OperatorType.X,
  [TokenId.PIXELS]: OperatorType.PIXELS,
};

/** Returns null on success, ErrorInfo on failure. */
function tokenize(expression: string, tokens: Token[]): ErrorInfo | null {
  expression = expression.toLowerCase();
  const parts = expression.split(/(mixed|px|[+*()^/%x-]|(?:\d+[.,]?\d*|[.,]?\d+)(?:[e][+-]?\d+)?|(?:\s+))/g);
  const count = parts.length;

  if (count === 0) {
    return { type: ErrorType.SYNTAX_EMPTY };
  }

  let position = 0;

  for (let i = 0; i < count; i++) {
    const part = parts[i];
    const length = part.length;

    // Inside split regex: token
    if (i % 2) {
      const id = Object.prototype.hasOwnProperty.call(textToTokenId, part) ? textToTokenId[part] : TokenId.NONE;
      const token: Token = {
        id,
        text: part,
        type: id !== TokenId.NONE ? TokenType.OPERATOR : TokenType.NONE,
        operator: tokenIdToOperatorType[id] ?? OperatorType.NONE,
        position,
        value: NaN,
      };

      if (token.type === TokenType.NONE) {
        if (/^(?:\d+[.,]?\d*|[.,]?\d+)(?:[eE][+-]?\d+)?$/.test(part)) {
          token.type = TokenType.NUMBER;
          token.value = +part.replace(',', '.');
        } else if (/(mixed)/.test(part)) {
          token.type = TokenType.MIXED_VARIABLE;
        }
      }

      if (token.type !== TokenType.NONE) {
        tokens.push(token);
      }
    }

    // Outside split regex: syntax error
    else if (length > 0) {
      return { type: ErrorType.SYNTAX_UNRECOGNIZED, position, length };
    }

    position += length;
  }

  if (tokens.length === 0) {
    return { type: ErrorType.SYNTAX_EMPTY };
  }

  // Post-process tokens to detect unary "+" and "-"
  for (let i = 0, previous: Token | null = null; i < tokens.length; i++) {
    const current = tokens[i];
    if (
      (current.operator === OperatorType.ADD || current.operator === OperatorType.SUBTRACT) &&
      (previous === null || comesBeforeUnaryOperator(previous.operator))
    ) {
      current.operator = current.operator === OperatorType.ADD ? OperatorType.UNARY_PLUS : OperatorType.UNARY_MINUS;
    }
    previous = current;
  }

  return null;
}

function comesBeforeUnaryOperator(type: OperatorType): boolean {
  const operator = operators[type];
  return (
    type === OperatorType.PAREN_L ||
    operator.degree === 2 ||
    (operator.degree === 1 && operator.associativity === Associativity.LEFT)
  );
}
